import React, { useEffect, useRef, useState } from "react";
import { renderToStaticMarkup } from "react-dom/server";
import QRCode from "qrcode";

import GloablButton from "../components/GloablButton";

import {
  StorefrontGeneralTabIcon,
  StorefrontBrandingTabIcon,
  StorefrontHoursTabIcon,
  StorefrontOperationsTabIcon,
  StorefrontQrTabIcon,
  StorefrontCustomerIcon,
  StorefrontDriverIcon,
  StorefrontQrCodeIcon,
} from "../icons/StorefrontQrIcons";

import LogoDakikaNos from "../icons/Arrows/LogoDakikaNos";

import vendorStorefrontController from "../controllers/vendorStorefrontController";
import { GROCERY_CATEGORIES } from "../registrationFlow/registrationHelpers";
import { userService } from "../services/userService";

import VendorLayout from "./VendorLayout";
import { getLanguage, translateLabel, translateText, useTranslationSnapshot } from "./../translations/runtime";

// Translate exact application messages; preserve arbitrary provider details.
const storefrontFeedbackLabels = new Map([
  ["Please select an image file.", "txt_web_vendor_please_select_an_image_file"],
  ["Image size must be 5MB or less.", "txt_web_vendor_image_size_must_be_5mb_or_less"],
  ["Unable to read selected image.", "txt_web_vendor_unable_to_read_selected_image"],
  ["Company id was not found for this user.", "txt_web_vendor_company_id_was_not_found_for_this_user"],
  ["Unable to save storefront settings.", "txt_web_vendor_unable_to_save_storefront_settings"],
  ["Unable to save storefront hours.", "txt_web_vendor_unable_to_save_storefront_hours"],
  ["Unable to load storefront settings.", "txt_web_vendor_unable_to_load_storefront_settings"],
  ["Unable to load driver QR code.", "txt_web_vendor_unable_to_load_driver_qr_code"],
  ["Authorization was not found for this user.", "txt_web_vendor_authorization_was_not_found_for_this_user"],
  ["Driver QR code is not available yet.", "txt_web_vendor_driver_qr_code_is_not_available_yet"],
]);
const translateStorefrontFeedback = (message) => {
  const id = storefrontFeedbackLabels.get(message);
  return id ? translateLabel(id, message) : message;
};

const escapePrintText = (value) => String(value).replace(/[&<>"']/g, (character) => ({
  "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;",
}[character]));


const STOREFRONT_TABS = [
  { label: "General", icon: StorefrontGeneralTabIcon },
  { label: "Branding", icon: StorefrontBrandingTabIcon },
  { label: "Hours & Availability", icon: StorefrontHoursTabIcon },
  { label: "Operations", icon: StorefrontOperationsTabIcon },
  { label: "QR Codes", icon: StorefrontQrTabIcon },
];

const STOREFRONT_TAB_LABELS = STOREFRONT_TABS.map(({ label }) => label);

const getInitialStorefrontTab = () => {
  const tabFromUrl = new URLSearchParams(window.location.search).get("tab");

  return STOREFRONT_TAB_LABELS.includes(tabFromUrl) ? tabFromUrl : "General";
};

const STORE_QR_CARDS = [
  {
    type: "customer",
    title: "QR Code for Customers",
    description:
      "Customers scan this QR to open your store in D2i2a W Noss and place orders.",
    icon: StorefrontCustomerIcon,
  },
  {
    type: "driver",
    title: "QR Code for Drivers",
    description:
      "D2i2a W Noss drivers scan this QR when arriving at your store for pickup or Butler processing.",
    icon: StorefrontDriverIcon,
  },
];

const CUISINE_GROUPS = [
  {
    title: "European Cuisines",
    items: [
      {
        label: "Italian - Pasta, pizza, risotto",
        marker: "italy",
        type: "flag",
      },
      {
        label: "French - Pastries, sauces, fine dining",
        marker: "france",
        type: "flag",
      },
    ],
  },
  {
    title: "Asian Cuisines",
    items: [
      {
        label: "Chinese - Stir fry, noodles, dumplings",
        marker: "china",
        type: "flag",
      },
      {
        label: "Japanese - Sushi, ramen, bento",
        marker: "japan",
        type: "flag",
      },
    ],
  },
  {
    title: "Middle Eastern & Arab Cuisines",
    items: [
      {
        label: "Lebanese - Mezze, hummus, tabbouleh",
        marker: "lebanon",
        type: "flag",
      },
      {
        label: "Turkish - Kebabs, baklava",
        marker: "turkey",
        type: "flag",
      },
    ],
  },
  {
    title: "American Cuisines",
    items: [
      {
        label: "American - Burgers, BBQ",
        marker: "usa",
        type: "flag",
      },
      {
        label: "Mexican - Tacos, burritos",
        marker: "mexico",
        type: "flag",
      },
    ],
  },
  {
    title: "Other Cuisines Categories",
    items: [
      {
        label: "Vegetarian / Vegan",
        marker: "V",
        type: "badge",
      },
      {
        label: "Fast Food",
        marker: "F",
        type: "badge",
      },
    ],
  },
];

const GROCERY_TYPE_GROUPS = [
  {
    title: "Grocery Categories",
    items: ["Grocery", ...GROCERY_CATEGORIES].map((label) => ({
      label,
      marker: "",
      type: "plain",
    })),
  },
];

const CUISINE_TYPE_SEPARATOR = " | ";

const CUISINE_OPTIONS = CUISINE_GROUPS.flatMap((group) => group.items);

const KNOWN_STORE_TYPES = new Set([
  ...CUISINE_OPTIONS, ...GROCERY_TYPE_GROUPS.flatMap((group) => group.items),
].map((item) => item.label));
const displayStoreType = (value) => KNOWN_STORE_TYPES.has(value) ? translateText(value) : value;

const textMentionsGrocery = (value) => {
  const normalizedValue = String(value || "").toLowerCase();

  return (
    normalizedValue.includes("grocery") ||
    normalizedValue.includes("grocer") ||
    normalizedValue.includes("crocer")
  );
};

const objectHasGroceryBusinessDescriptor = (source, seen = new WeakSet()) => {
  if (!source) return false;
  if (typeof source === "string") return textMentionsGrocery(source);
  if (typeof source !== "object") return false;
  if (seen.has(source)) return false;
  seen.add(source);

  if (Array.isArray(source)) {
    return source.some((item) =>
      objectHasGroceryBusinessDescriptor(item, seen),
    );
  }

  return Object.entries(source).some(([key, value]) => {
    const normalizedKey = String(key || "").toLowerCase();
    const isBusinessDescriptorKey =
      normalizedKey.includes("business") ||
      normalizedKey.includes("profile") ||
      normalizedKey.includes("category") ||
      normalizedKey.includes("type") ||
      normalizedKey.includes("portal");

    if (isBusinessDescriptorKey && textMentionsGrocery(value)) return true;

    return objectHasGroceryBusinessDescriptor(value, seen);
  });
};

const isGroceryVendorPortal = () =>
  textMentionsGrocery(window.location.pathname) ||
  objectHasGroceryBusinessDescriptor(userService.getUserPayload?.()) ||
  objectHasGroceryBusinessDescriptor(userService.getUserObject?.()) ||
  objectHasGroceryBusinessDescriptor(userService.getUserData?.());

const normalizeCuisineKey = (value) =>
  String(value || "")
    .trim()
    .toLowerCase()
    .replace(/&/g, "and")
    .replace(/[–—]/g, "-")
    .replace(/[^a-z0-9]+/g, " ")
    .replace(/\s+/g, " ")
    .trim();

const getCuisineBaseName = (value) =>
  String(value || "")
    .split(/\s+-\s+/)[0]
    .trim();

const getRawCuisineValue = (item) => {
  if (typeof item === "string" || typeof item === "number") {
    return String(item);
  }

  if (!item || typeof item !== "object") {
    return "";
  }

  return (
    item.label || item.name || item.cuisineType || item.type || item.value || ""
  );
};

const getCanonicalCuisineLabel = (value) => {
  const rawValue = getRawCuisineValue(value).trim();

  if (!rawValue) {
    return "";
  }

  const normalizedValue = normalizeCuisineKey(rawValue);

  const exactMatch = CUISINE_OPTIONS.find(
    (item) => normalizeCuisineKey(item.label) === normalizedValue,
  );

  if (exactMatch) {
    return exactMatch.label;
  }

  const baseNameMatch = CUISINE_OPTIONS.find((item) => {
    const optionBaseName = getCuisineBaseName(item.label);

    return normalizeCuisineKey(optionBaseName) === normalizedValue;
  });

  if (baseNameMatch) {
    return baseNameMatch.label;
  }

  const rawBaseName = getCuisineBaseName(rawValue);

  const descriptiveMatch = CUISINE_OPTIONS.find((item) => {
    const optionBaseName = getCuisineBaseName(item.label);

    return (
      normalizeCuisineKey(optionBaseName) === normalizeCuisineKey(rawBaseName)
    );
  });

  if (descriptiveMatch) {
    return descriptiveMatch.label;
  }

  const aliases = {
    italian: "Italian - Pasta, pizza, risotto",
    french: "French - Pastries, sauces, fine dining",
    chinese: "Chinese - Stir fry, noodles, dumplings",
    japanese: "Japanese - Sushi, ramen, bento",
    lebanese: "Lebanese - Mezze, hummus, tabbouleh",
    turkish: "Turkish - Kebabs, baklava",
    american: "American - Burgers, BBQ",
    mexican: "Mexican - Tacos, burritos",
    vegetarian: "Vegetarian / Vegan",
    vegan: "Vegetarian / Vegan",
    "vegetarian vegan": "Vegetarian / Vegan",
    "vegetarian and vegan": "Vegetarian / Vegan",
    "fast food": "Fast Food",
    fastfood: "Fast Food",
  };

  return aliases[normalizedValue] || rawValue;
};

const parseCuisineTypes = (value) => {
  let values = [];

  if (Array.isArray(value)) {
    values = value;
  } else {
    const rawValue = String(value || "").trim();

    if (!rawValue) {
      return [];
    }

    values = rawValue.split(/\s*\|\s*/);
  }

  const normalizedValues = values
    .map((item) => getCanonicalCuisineLabel(item))
    .map((item) => String(item || "").trim())
    .filter(Boolean);

  return [...new Set(normalizedValues)];
};

const getOpenStoreTypeGroups = (selectedTypes, storeTypeGroups) => {
  const selectedTypeKeys = new Set(
    selectedTypes.map((type) => normalizeCuisineKey(type)).filter(Boolean),
  );

  if (selectedTypeKeys.size === 0) return [];

  return storeTypeGroups
    .filter((group) =>
      group.items.some((item) => {
        const itemLabelKey = normalizeCuisineKey(item.label);
        const itemBaseKey = normalizeCuisineKey(getCuisineBaseName(item.label));

        return (
          selectedTypeKeys.has(itemLabelKey) ||
          selectedTypeKeys.has(itemBaseKey)
        );
      }),
    )
    .map((group) => group.title);
};

const getCuisineLabel = (value) => {
  const selectedCuisines = parseCuisineTypes(value);

  if (selectedCuisines.length === 0) {
    return translateLabel("txt_web_vendor_select_cuisine_type", "Select cuisine type");
  }

  if (selectedCuisines.length === 1) {
    return displayStoreType(selectedCuisines[0]);
  }

  return translateLabel("txt_web_vendor_selected_cuisine_count", "{count} cuisine types selected", { count: selectedCuisines.length });
};

const getStoreTypeLabel = (value, isGroceryVendor) => {
  const selectedTypes = parseCuisineTypes(value);

  if (!isGroceryVendor) return getCuisineLabel(value);

  if (selectedTypes.length === 0) {
    return translateLabel("txt_web_vendor_select_grocery_type", "Select grocery type");
  }

  if (selectedTypes.length === 1) {
    return displayStoreType(selectedTypes[0]);
  }

  return translateLabel("txt_web_vendor_selected_grocery_count", "{count} grocery types selected", { count: selectedTypes.length });
};

const WEEK_DAYS = [
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
  "Sunday",
];

const DAY_INDEX_TO_NAME = {
  0: "Sunday",
  1: "Monday",
  2: "Tuesday",
  3: "Wednesday",
  4: "Thursday",
  5: "Friday",
  6: "Saturday",
  7: "Sunday",
};

const DAY_NAME_TO_API_INDEX = {
  Monday: 1,
  Tuesday: 2,
  Wednesday: 3,
  Thursday: 4,
  Friday: 5,
  Saturday: 6,
  Sunday: 0,
};

const createDefaultHours = () =>
  WEEK_DAYS.reduce((days, day) => {
    days[day] = {
      businessHourId: "",
      open: true,
      start: "",
      end: "",
    };

    return days;
  }, {});

const DEFAULT_HOURS = createDefaultHours();

const unwrapPayload = (response) =>
  response?.data?.data ?? response?.data?.result ?? response?.data ?? {};

const formatApiTime = (time) => {
  if (typeof time === "string") {
    const [hour = "", minute = ""] = time.split(":");

    if (!hour || !minute) return "";

    return `${hour.padStart(2, "0")}:${minute.padStart(2, "0")}`;
  }

  if (!time || typeof time !== "object") {
    return "";
  }

  const hour = Number(time.hour);
  const minute = Number(time.minute);

  if (!Number.isFinite(hour) || !Number.isFinite(minute)) {
    return "";
  }

  return `${String(hour).padStart(2, "0")}:${String(minute).padStart(2, "0")}`;
};

const isPresent = (value) =>
  value !== undefined && value !== null && value !== "";

const hasOwn = (object, key) =>
  Object.prototype.hasOwnProperty.call(object || {}, key);

const getNullableString = (value) => {
  const stringValue = String(value ?? "").trim();

  return stringValue || null;
};

const getNullableNumber = (value) => {
  if (!isPresent(value)) return null;

  const numberValue = Number(value);

  return Number.isFinite(numberValue) ? numberValue : null;
};

const isIdValue = (value) => typeof value === "string" && value.trim() !== "";

const getBusinessHourId = (hourItem = {}) => {
  const directId =
    hourItem.businessHourId ||
    hourItem.businessHourID ||
    hourItem.businessHourUUID ||
    hourItem.businessHoursId ||
    hourItem.businessHoursID ||
    hourItem.businessHoursUUID ||
    hourItem.business_hourId ||
    hourItem.business_hour_id ||
    hourItem.business_hours_id ||
    hourItem.business_hoursId ||
    hourItem.hourId ||
    hourItem.hourID ||
    hourItem.hoursId ||
    hourItem.hoursID ||
    hourItem.uuid ||
    hourItem.id;

  if (isIdValue(directId)) {
    return directId;
  }

  const matchedEntry = Object.entries(hourItem).find(([key, value]) => {
    if (!isIdValue(value)) return false;

    const normalizedKey = key.toLowerCase().replace(/[^a-z0-9]/g, "");

    return (
      (normalizedKey.includes("business") &&
        normalizedKey.includes("hour") &&
        normalizedKey.endsWith("id")) ||
      normalizedKey === "businesshouruuid" ||
      normalizedKey === "businesshoursuuid"
    );
  });

  return matchedEntry?.[1] || "";
};

const getApiDayOfWeek = (hourItem = {}) => {
  const rawDay =
    hourItem.dayOfWeek ??
    hourItem.day ??
    hourItem.weekDay ??
    hourItem.weekday ??
    hourItem.dayName;

  const numericDay = Number(rawDay);

  if (Number.isFinite(numericDay)) {
    return numericDay === 7 ? 0 : numericDay;
  }

  const normalizedDay = String(rawDay || "")
    .trim()
    .toLowerCase();

  const dayName = WEEK_DAYS.find(
    (weekDay) => weekDay.toLowerCase() === normalizedDay,
  );

  return dayName ? DAY_NAME_TO_API_INDEX[dayName] : undefined;
};

const HOUR_LIST_KEYS = [
  "hours",
  "businessHours",
  "businessHour",
  "storeHours",
  "openingHours",
  "availabilityHours",
  "operatingHours",
  "operationHours",
  "workingHours",
];

const normalizeHoursCollection = (value) => {
  if (Array.isArray(value)) return value;

  if (!value || typeof value !== "object") {
    return [];
  }

  const values = Object.values(value);

  return values.every((item) => item && typeof item === "object") ? values : [];
};

const isHourLikeItem = (item) =>
  Boolean(
    item &&
    typeof item === "object" &&
    !Array.isArray(item) &&
    (item.dayOfWeek !== undefined ||
      item.openTime !== undefined ||
      item.closeTime !== undefined),
  );

const isHourList = (value) => {
  const hours = normalizeHoursCollection(value);

  return hours.length > 0 && hours.some(isHourLikeItem);
};

const getHoursList = (payload = {}) => {
  const seen = new Set();

  const findHoursList = (value) => {
    if (!value || typeof value !== "object" || seen.has(value)) {
      return [];
    }

    seen.add(value);

    if (isHourList(value)) {
      return normalizeHoursCollection(value);
    }

    for (const key of HOUR_LIST_KEYS) {
      if (isHourList(value[key])) {
        return normalizeHoursCollection(value[key]);
      }
    }

    for (const childValue of Object.values(value)) {
      const nestedHours = findHoursList(childValue);

      if (nestedHours.length > 0) {
        return nestedHours;
      }
    }

    return [];
  };

  return findHoursList(payload);
};

const getBusinessHourIdsByDay = (apiHours = []) =>
  apiHours.reduce((idsByDay, hourItem) => {
    const dayOfWeek = getApiDayOfWeek(hourItem);

    const businessHourId = getBusinessHourId(hourItem);

    if (Number.isFinite(dayOfWeek) && businessHourId) {
      idsByDay[dayOfWeek] = businessHourId;
    }

    return idsByDay;
  }, {});
const normalizeHours = (apiHours) => {
  const nextHours = createDefaultHours();

  if (!Array.isArray(apiHours)) return nextHours;

  const groupedHours = {};

  apiHours.forEach((hourItem) => {
    const day = DAY_INDEX_TO_NAME[getApiDayOfWeek(hourItem)];

    if (!day) return;

    groupedHours[day] = [...(groupedHours[day] || []), hourItem];
  });

  Object.entries(groupedHours).forEach(([day, dayItems]) => {
    const openItems = dayItems
      .filter((hourItem) => Boolean(hourItem?.open))
      .sort((leftItem, rightItem) =>
        formatApiTime(leftItem?.openTime).localeCompare(
          formatApiTime(rightItem?.openTime),
        ),
      );

    const firstItem = openItems[0] || dayItems[0];

    const lastItem = openItems[openItems.length - 1] || firstItem;

    nextHours[day] = {
      businessHourId: getBusinessHourId(firstItem),

      open: openItems.length > 0 || Boolean(firstItem?.open),

      start: formatApiTime(firstItem?.openTime),

      end: formatApiTime(lastItem?.closeTime),
    };
  });

  return nextHours;
};

const toSettingsTime = (value) => {
  const time = formatApiTime(value);

  return time ? `${time}:00` : null;
};

const buildSettingsHoursPayload = (hours, businessHourIdsByDay = {}) =>
  WEEK_DAYS.map((day) => {
    const dayHours = hours[day] || {};

    const dayOfWeek = DAY_NAME_TO_API_INDEX[day];

    const openTime = toSettingsTime(dayHours.start);

    const closeTime = toSettingsTime(dayHours.end);

    if (!openTime || !closeTime) {
      return null;
    }

    const businessHourId =
      getBusinessHourId(dayHours) || businessHourIdsByDay[dayOfWeek] || "";

    const hourPayload = {
      closeTime,
      dayOfWeek,
      open: Boolean(dayHours.open),
      openTime,
    };

    if (businessHourId) {
      hourPayload.businessHourId = businessHourId;
    }

    return hourPayload;
  }).filter(Boolean);

const resizeImageFile = (
  file,
  {
    maxDataUrlLength,
    maxWidth,
    maxHeight,
    outputWidth,
    outputHeight,
    quality = 0.45,
  } = {},
) =>
  new Promise((resolve, reject) => {
    const image = new Image();

    const objectUrl = URL.createObjectURL(file);

    image.onload = () => {
      const canvas = document.createElement("canvas");

      const context = canvas.getContext("2d");

      const hasExactSize = outputWidth && outputHeight;

      if (hasExactSize) {
        const scale = Math.max(
          outputWidth / image.width,
          outputHeight / image.height,
        );

        const drawWidth = image.width * scale;

        const drawHeight = image.height * scale;

        const drawX = (outputWidth - drawWidth) / 2;

        const drawY = (outputHeight - drawHeight) / 2;

        canvas.width = outputWidth;
        canvas.height = outputHeight;

        context.imageSmoothingEnabled = true;

        context.imageSmoothingQuality = "high";

        context.fillStyle = "#ffffff";

        context.fillRect(0, 0, outputWidth, outputHeight);

        context.drawImage(image, drawX, drawY, drawWidth, drawHeight);
      } else {
        const scale = Math.min(
          maxWidth / image.width,
          maxHeight / image.height,
          1,
        );

        const width = Math.max(1, Math.round(image.width * scale));

        const height = Math.max(1, Math.round(image.height * scale));

        canvas.width = width;
        canvas.height = height;

        context.imageSmoothingEnabled = true;

        context.imageSmoothingQuality = "high";

        context.fillStyle = "#ffffff";

        context.fillRect(0, 0, width, height);

        context.drawImage(image, 0, 0, width, height);
      }

      const qualitySteps = [quality, 0.22, 0.16, 0.1, 0.06];

      const imageDataUrl =
        qualitySteps
          .map((nextQuality) => canvas.toDataURL("image/jpeg", nextQuality))
          .find(
            (nextDataUrl) =>
              !maxDataUrlLength || nextDataUrl.length <= maxDataUrlLength,
          ) || canvas.toDataURL("image/jpeg", 0.16);

      URL.revokeObjectURL(objectUrl);

      resolve(imageDataUrl);
    };

    image.onerror = () => {
      URL.revokeObjectURL(objectUrl);

      reject(new Error("Unable to read selected image."));
    };

    image.src = objectUrl;
  });

const getImagePreviewSrc = (value) => {
  if (!isPresent(value)) {
    return undefined;
  }

  if (/^(data:image\/|https?:\/\/|blob:|\/)/i.test(value)) {
    return value;
  }

  return `data:image/jpeg;base64,${value}`;
};

const getImageApiValue = (value) => {
  const imageValue = String(value || "").trim();

  if (!imageValue) return "";

  if (/^(data:image\/|https?:\/\/|\/)/i.test(imageValue)) {
    return imageValue;
  }

  if (/^[A-Za-z0-9+/=]+$/.test(imageValue) && imageValue.length > 100) {
    return imageValue;
  }

  return "";
};

/* ============================
   DRIVER QR HELPERS
   React Web
============================ */

const getGeneratedQrImageUri = (value) =>
  QRCode.toDataURL(String(value || ""), {
    errorCorrectionLevel: "H",
    margin: 1,
    scale: 8,
  });

const loadImageForCanvas = (src) =>
  new Promise((resolve, reject) => {
    const image = new Image();

    image.onload = () => resolve(image);
    image.onerror = reject;
    image.crossOrigin = "anonymous";
    image.src = src;
  });

const getDakikaNosLogoImageUri = () => {
  const svgMarkup = renderToStaticMarkup(<LogoDakikaNos />);

  return `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svgMarkup)}`;
};

const downloadQrWithBranding = async ({ fileName, qrImageUri, storeName }) => {
  if (!qrImageUri) return;

  const safeStoreName = String(storeName || "Store").trim() || "Store";
  const canvas = document.createElement("canvas");
  const width = 520;
  const height = 580;
  const qrSize = 360;
  const qrX = (width - qrSize) / 2;
  const qrY = 118;
  const context = canvas.getContext("2d");
  const fillRoundedRect = (x, y, width, height, radius) => {
    if (typeof context.roundRect === "function") {
      context.beginPath();
      context.roundRect(x, y, width, height, radius);
      context.fill();
      return;
    }

    context.fillRect(x, y, width, height);
  };

  canvas.width = width;
  canvas.height = height;

  context.fillStyle = "#ffffff";
  context.fillRect(0, 0, width, height);

  context.fillStyle = "#111827";
  context.font = "700 30px Arial, sans-serif";
  context.textAlign = "center";
  context.textBaseline = "middle";
  context.fillText(safeStoreName, width / 2, 56, width - 48);

  const qrImage = await loadImageForCanvas(qrImageUri);
  context.drawImage(qrImage, qrX, qrY, qrSize, qrSize);

  const logoImage = await loadImageForCanvas(getDakikaNosLogoImageUri());
  const logoWidth = 126;
  const logoHeight = Math.round((logoWidth * 34) / 97);
  const logoPaddingX = 14;
  const logoPaddingY = 12;
  const logoBoxWidth = logoWidth + logoPaddingX * 2;
  const logoBoxHeight = logoHeight + logoPaddingY * 2;
  const logoBoxX = (width - logoBoxWidth) / 2;
  const logoBoxY = qrY + qrSize + 22;

  context.fillStyle = "#ffffff";
  fillRoundedRect(logoBoxX, logoBoxY, logoBoxWidth, logoBoxHeight, 14);
  context.drawImage(
    logoImage,
    logoBoxX + logoPaddingX,
    logoBoxY + logoPaddingY,
    logoWidth,
    logoHeight,
  );

  const link = document.createElement("a");
  link.href = canvas.toDataURL("image/png");
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

const StoreLogoUploadIcon = () => { useTranslationSnapshot(); return  (
  <svg
    className="vendor-storefront-logo-icon"
    width="40"
    height="40"
    viewBox="0 0 40 40"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
  >
    <path
      d="M3.33252 11.664L10.6808 4.31563C10.9909 4.00375 11.3596 3.75632 11.7657 3.58759C12.1717 3.41886 12.6072 3.33217 13.047 3.33252H26.9438C27.3836 3.33217 27.819 3.41886 28.2251 3.58759C28.6312 3.75632 28.9999 4.00375 29.3099 4.31563L36.6583 11.664"
      stroke="white"
      strokeWidth="3.33257"
      strokeLinecap="round"
      strokeLinejoin="round"
    />

    <path
      d="M6.66516 19.9954V33.3257C6.66516 34.2095 7.01627 35.0572 7.64125 35.6821C8.26623 36.3071 9.11388 36.6582 9.99774 36.6582H29.9932C30.877 36.6582 31.7247 36.3071 32.3497 35.6821C32.9746 35.0572 33.3258 34.2095 33.3258 33.3257V19.9954"
      stroke="white"
      strokeWidth="3.33257"
      strokeLinecap="round"
      strokeLinejoin="round"
    />

    <path
      d="M24.9943 36.6584V29.9932C24.9943 29.1094 24.6432 28.2617 24.0182 27.6367C23.3932 27.0118 22.5456 26.6606 21.6617 26.6606H18.3292C17.4453 26.6606 16.5977 27.0118 15.9727 27.6367C15.3477 28.2617 14.9966 29.1094 14.9966 29.9932V36.6584"
      stroke="white"
      strokeWidth="3.33257"
      strokeLinecap="round"
      strokeLinejoin="round"
    />

    <path
      d="M3.33252 11.6641H36.6583"
      stroke="white"
      strokeWidth="3.33257"
      strokeLinecap="round"
      strokeLinejoin="round"
    />

    <path
      d="M36.6583 11.6641V16.6629C36.6583 17.5468 36.3072 18.3944 35.6822 19.0194C35.0572 19.6444 34.2095 19.9955 33.3257 19.9955C32.3521 19.9419 31.4223 19.5735 30.6763 18.9457C30.4775 18.8021 30.2384 18.7247 29.9931 18.7247C29.7478 18.7247 29.5088 18.8021 29.3099 18.9457C28.5639 19.5735 27.6341 19.9419 26.6605 19.9955C25.687 19.9419 24.7572 19.5735 24.0111 18.9457C23.8123 18.8021 23.5733 18.7247 23.328 18.7247C23.0827 18.7247 22.8436 18.8021 22.6448 18.9457C21.8987 19.5735 20.9689 19.9419 19.9954 19.9955C19.0218 19.9419 18.092 19.5735 17.346 18.9457C17.1472 18.8021 16.9081 18.7247 16.6628 18.7247C16.4175 18.7247 16.1785 18.8021 15.9796 18.9457C15.2336 19.5735 14.3038 19.9419 13.3302 19.9955C12.3567 19.9419 11.4269 19.5735 10.6808 18.9457C10.482 18.8021 10.243 18.7247 9.99767 18.7247C9.75237 18.7247 9.51331 18.8021 9.31449 18.9457C8.56844 19.5735 7.63865 19.9419 6.66509 19.9955C5.78124 19.9955 4.93359 19.6444 4.30861 19.0194C3.68363 18.3944 3.33252 17.5468 3.33252 16.6629V11.6641"
      stroke="white"
      strokeWidth="3.33257"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
); };

const CoverImageUploadIcon = () => { useTranslationSnapshot(); return  (
  <svg
    className="vendor-storefront-cover-icon"
    width="48"
    height="48"
    viewBox="0 0 48 48"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
  >
    <path
      d="M37.9914 5.99854H9.99775C7.78911 5.99854 5.99866 7.78899 5.99866 9.99763V37.9913C5.99866 40.1999 7.78911 41.9903 9.99775 41.9903H37.9914C40.2 41.9903 41.9905 40.1999 41.9905 37.9913V9.99763C41.9905 7.78899 40.2 5.99854 37.9914 5.99854Z"
      stroke="#90A1B9"
      strokeWidth="3.99909"
      strokeLinecap="round"
      strokeLinejoin="round"
    />

    <path
      d="M17.9959 21.995C20.2046 21.995 21.995 20.2046 21.995 17.9959C21.995 15.7873 20.2046 13.9968 17.9959 13.9968C15.7873 13.9968 13.9968 15.7873 13.9968 17.9959C13.9968 20.2046 15.7873 21.995 17.9959 21.995Z"
      stroke="#90A1B9"
      strokeWidth="3.99909"
      strokeLinecap="round"
      strokeLinejoin="round"
    />

    <path
      d="M41.9905 29.9931L35.8199 23.8225C35.07 23.0728 34.053 22.6516 32.9925 22.6516C31.9321 22.6516 30.9151 23.0728 30.1652 23.8225L11.9973 41.9904"
      stroke="#90A1B9"
      strokeWidth="3.99909"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
); };

const SAVE_CHANGES_BUTTON_PROPS = {
  width: "119.37px",
  maxWidth: "none",
  height: "35.98px",
  background: "#F68934",
  borderRadius: "8px",
  fontSize: "14px",
  fontWeight: 600,
  lineHeight: "20px",
  padding: "8px 16px",
  gap: "8px",
  buttonFlex: "none",
  buttonOrder: 1,
  buttonFlexGrow: 0,
  textWidth: "97px",
  textHeight: "20px",
  textFlex: "none",
  textOrder: 0,
  textFlexGrow: 0,
  textOverflow: "visible",
  textOverflowMode: "clip",
};

const CANCEL_BUTTON_PROPS = {
  width: "117px",
  maxWidth: "none",
  height: "36px",
  background: "#FFFFFF",
  border: "1.21013px solid rgba(0, 0, 0, 0.1)",
  borderRadius: "8px",
  color: "#000000",
  fontSize: "14px",
  fontWeight: 500,
  lineHeight: "20px",
  padding: "8px 16px",
  gap: "8px",
  buttonFlex: "none",
  buttonOrder: 0,
  buttonFlexGrow: 0,
  textWidth: "47px",
  textHeight: "20px",
  textFlex: "none",
  textOrder: 0,
  textFlexGrow: 0,
  textOverflow: "visible",
  textOverflowMode: "clip",
};

const UPLOAD_MEDIA_BUTTON_PROPS = {
  width: "148.68px",
  maxWidth: "none",
  height: "35.98px",
  background: "#FFFFFF",
  border: "1.21013px solid rgba(0, 0, 0, 0.1)",
  borderRadius: "8px",
  color: "#000000",
  fontSize: "14px",
  fontWeight: 500,
  lineHeight: "20px",
  padding: "8px 16px",
  gap: "8px",
  buttonFlex: "none",
  buttonOrder: 0,
  buttonFlexGrow: 0,
  textHeight: "20px",
  textFlex: "none",
  textOrder: 0,
  textFlexGrow: 0,
  textOverflow: "visible",
  textOverflowMode: "clip",
};

function VendorStorefront() {
  useTranslationSnapshot();
  const isGroceryVendor =
    userService.getUserData().BType === "GROCERIES" ? true : false;
  // isGroceryVendorPortal();
  const storeTypeGroups = isGroceryVendor
    ? GROCERY_TYPE_GROUPS
    : CUISINE_GROUPS;
  const storeTypeLabel = isGroceryVendor ? "Grocery Type" : "Cuisine Type";
  const [activeTab, setActiveTab] = useState(getInitialStorefrontTab);

  const [storeOnline, setStoreOnline] = useState(true);

  const [isCuisineDropdownOpen, setIsCuisineDropdownOpen] = useState(false);

  const [openCuisineGroups, setOpenCuisineGroups] = useState([]);

  const [selectedCuisineTypes, setSelectedCuisineTypes] = useState([]);

  const [storefrontForm, setStorefrontForm] = useState({
    businessDescription: "",
    cuisineType: "",
    deliveryFee: "",
    logoUrl: "",
    coverImageUrl: "",
    minimumOrderValue: "",
    newOrdersPaused: false,
    preparationTimeMinutes: "",
    priceRange: "",
    rating: "",
    settingsId: "",
    storeName: "",
    temporaryClosureReason: "",
    verticalType: "",
  });

  const [hours, setHours] = useState(DEFAULT_HOURS);

  const [settingsError, setSettingsError] = useState("");

  const [customerQrImageUri] = useState("");

  // DRIVER QR
  const [driverQrImageUri, setDriverQrImageUri] = useState("");

  const [isLoadingDriverQr, setIsLoadingDriverQr] = useState(false);

  const [driverQrError, setDriverQrError] = useState("");

  const [failedImages, setFailedImages] = useState({});

  const [isSavingSettings, setIsSavingSettings] = useState(false);

  const [isSavingHours, setIsSavingHours] = useState(false);

  const cuisineDropdownRef = useRef(null);

  const logoInputRef = useRef(null);

  const coverInputRef = useRef(null);

  const businessHourIdsByDayRef = useRef({});

  const savedStorefrontFormRef = useRef(null);

  const savedHoursRef = useRef(DEFAULT_HOURS);

  const hasInitializedCuisineGroupsRef = useRef(false);

  const updateHour = (day, field, value) => {
    setHours((currentHours) => ({
      ...currentHours,

      [day]: {
        ...currentHours[day],
        [field]: value,
      },
    }));
  };

  const updateFormField = (field) => (event) => {
    setStorefrontForm((currentForm) => ({
      ...currentForm,
      [field]: event.target.value,
    }));
  };

  const selectCuisineType = (value) => {
    setSelectedCuisineTypes((currentTypes) => {
      const nextTypes = currentTypes.includes(value)
        ? currentTypes.filter((item) => item !== value)
        : [...currentTypes, value];

      setStorefrontForm((currentForm) => ({
        ...currentForm,

        cuisineType: nextTypes.join(CUISINE_TYPE_SEPARATOR),
      }));

      return nextTypes;
    });
  };

  const toggleCuisineGroup = (groupTitle) => {
    setOpenCuisineGroups((currentGroups) =>
      currentGroups.includes(groupTitle)
        ? currentGroups.filter((title) => title !== groupTitle)
        : [...currentGroups, groupTitle],
    );
  };

  const updateImageField = (field, options) => async (event) => {
    const [file] = event.target.files || [];

    if (!file) return;

    if (!file.type.startsWith("image/")) {
      setSettingsError("Please select an image file.");

      event.target.value = "";

      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      setSettingsError("Image size must be 5MB or less.");

      event.target.value = "";

      return;
    }

    try {
      const imageDataUrl = await resizeImageFile(file, options);

      setSettingsError("");

      setFailedImages((currentFailedImages) => ({
        ...currentFailedImages,
        [field]: false,
      }));

      setStorefrontForm((currentForm) => ({
        ...currentForm,
        [field]: imageDataUrl,
      }));
    } catch (error) {
      setSettingsError(error?.message || "Unable to read selected image.");
    } finally {
      event.target.value = "";
    }
  };

  const triggerLogoUpload = () => {
    logoInputRef.current?.click();
  };

  const triggerCoverUpload = () => {
    coverInputRef.current?.click();
  };

  const renderLogoUpload = () => (
    <div
      className={`vendor-storefront-logo-upload ${
        storefrontForm.logoUrl ? "has-image" : ""
      }`}
      role="button"
      aria-label={translateLabel("btn_upload_new_logo", "Upload New Logo")}
      tabIndex={0}
      onClick={triggerLogoUpload}
      onKeyDown={(event) => {
        if (event.key === "Enter" || event.key === " ") {
          event.preventDefault();

          triggerLogoUpload();
        }
      }}
    >
      {storefrontForm.logoUrl && !failedImages.logoUrl ? (
        <img
          alt={translateLabel("txt_web_vendor_store_logo_preview", "Store logo preview")}
          src={storefrontForm.logoUrl}
          onError={() =>
            setFailedImages((currentFailedImages) => ({
              ...currentFailedImages,
              logoUrl: true,
            }))
          }
        />
      ) : (
        <StoreLogoUploadIcon />
      )}
    </div>
  );

  const renderCoverUpload = () => (
    <div
      className={`vendor-storefront-cover-upload ${
        storefrontForm.coverImageUrl ? "has-image" : ""
      }`}
      role="button"
      aria-label={translateLabel("btn_upload_cover_image", "Upload Cover Image")}
      tabIndex={0}
      onClick={triggerCoverUpload}
      onKeyDown={(event) => {
        if (event.key === "Enter" || event.key === " ") {
          event.preventDefault();

          triggerCoverUpload();
        }
      }}
    >
      {storefrontForm.coverImageUrl && !failedImages.coverImageUrl ? (
        <img
          alt={translateLabel("txt_web_vendor_store_cover_preview", "Store cover preview")}
          src={storefrontForm.coverImageUrl}
          onError={() =>
            setFailedImages((currentFailedImages) => ({
              ...currentFailedImages,
              coverImageUrl: true,
            }))
          }
        />
      ) : (
        <CoverImageUploadIcon />
      )}
    </div>
  );
  const mergeBusinessHourIdsByDay = (payloadHours = []) => {
    const nextBusinessHourIdsByDay = getBusinessHourIdsByDay(payloadHours);

    if (Object.keys(nextBusinessHourIdsByDay).length > 0) {
      businessHourIdsByDayRef.current = {
        ...businessHourIdsByDayRef.current,
        ...nextBusinessHourIdsByDay,
      };
    }

    return nextBusinessHourIdsByDay;
  };

  const applySettingsPayload = (payload, companyId) => {
    setStorefrontForm((currentForm) => {
      const nextForm = {
        ...currentForm,

        businessDescription:
          payload?.businessDescription ?? currentForm.businessDescription,

        companyId: payload?.companyId || companyId || currentForm.companyId,

        coverImageUrl: hasOwn(payload, "coverImageUrl")
          ? getImagePreviewSrc(payload.coverImageUrl) || ""
          : currentForm.coverImageUrl,

        cuisineType: payload?.cuisineType ?? currentForm.cuisineType,

        deliveryFee: payload?.deliveryFee ?? currentForm.deliveryFee,

        logoUrl: hasOwn(payload, "logoUrl")
          ? getImagePreviewSrc(payload.logoUrl) || ""
          : currentForm.logoUrl,

        minimumOrderValue:
          payload?.minimumOrderValue ?? currentForm.minimumOrderValue,

        newOrdersPaused:
          payload?.newOrdersPaused ?? currentForm.newOrdersPaused,

        preparationTimeMinutes:
          payload?.preparationTimeMinutes ?? currentForm.preparationTimeMinutes,

        priceRange: payload?.priceRange ?? currentForm.priceRange,

        rating: payload?.rating ?? currentForm.rating,

        settingsId: payload?.settingsId || currentForm.settingsId,

        storeName: payload?.storeName ?? currentForm.storeName,

        temporaryClosureReason:
          payload?.temporaryClosureReason ?? currentForm.temporaryClosureReason,

        verticalType: payload?.verticalType ?? currentForm.verticalType,
      };

      savedStorefrontFormRef.current = nextForm;

      const nextSelectedCuisineTypes = parseCuisineTypes(nextForm.cuisineType);

      setSelectedCuisineTypes(nextSelectedCuisineTypes);

      if (!hasInitializedCuisineGroupsRef.current) {
        setOpenCuisineGroups(
          getOpenStoreTypeGroups(nextSelectedCuisineTypes, storeTypeGroups),
        );
        hasInitializedCuisineGroupsRef.current = true;
      }

      return nextForm;
    });

    if (payload?.storeOnline !== undefined && payload?.storeOnline !== null) {
      setStoreOnline(Boolean(payload.storeOnline));
    }

    const payloadHours = getHoursList(payload);

    if (payloadHours.length > 0) {
      mergeBusinessHourIdsByDay(payloadHours);

      const nextHours = normalizeHours(payloadHours);

      savedHoursRef.current = nextHours;

      setHours(nextHours);
    } else {
      console.log(
        "[Storefront Hours] no hours list found in settings payload",
        {
          payload,
        },
      );
    }
  };

  const getSettingsRequest = (
    companyId,
    { includeSettingsId = false } = {},
  ) => {
    const request = {
      businessDescription: getNullableString(
        storefrontForm.businessDescription,
      ),

      companyId,

      coverImageUrl: getImageApiValue(storefrontForm.coverImageUrl) || null,

      cuisineType: getNullableString(storefrontForm.cuisineType),

      deliveryFee: getNullableNumber(storefrontForm.deliveryFee),

      hours: buildSettingsHoursPayload(hours, businessHourIdsByDayRef.current),

      logoUrl: getImageApiValue(storefrontForm.logoUrl) || null,

      minimumOrderValue: getNullableNumber(storefrontForm.minimumOrderValue),

      newOrdersPaused: Boolean(storefrontForm.newOrdersPaused),

      preparationTimeMinutes: getNullableNumber(
        storefrontForm.preparationTimeMinutes,
      ),

      priceRange: getNullableString(storefrontForm.priceRange),

      rating: getNullableNumber(storefrontForm.rating),

      settingsId: includeSettingsId ? storefrontForm.settingsId : null,

      storeName: getNullableString(storefrontForm.storeName),

      storeOnline,

      temporaryClosureReason: getNullableString(
        storefrontForm.temporaryClosureReason,
      ),

      verticalType: getNullableString(storefrontForm.verticalType),
    };

    return request;
  };

  const saveStorefrontSettings = async ({
    fallbackError = "Unable to save storefront settings.",
    setSavingState = setIsSavingSettings,
  } = {}) => {
    setSettingsError("");
    setSavingState(true);

    try {
      const Authorization = userService.getJwt();
        const companyId = userService.getUserData().Compid;

      if (!companyId) {
        throw new Error("Company id was not found for this user.");
      }

      const request = getSettingsRequest(companyId, {
        includeSettingsId: Boolean(storefrontForm.settingsId),
      });

      const response = await vendorStorefrontController.saveSettings({
        Authorization,
        companyId,
        request,
      });

      if (response?.isAxiosError || response?.response) {
        throw response;
      }

      const payload = unwrapPayload(response);

      const nextPayload =
        payload && typeof payload === "object" && !Array.isArray(payload)
          ? payload
          : request;

      applySettingsPayload(nextPayload, companyId);
    } catch (requestError) {
      setSettingsError(
        requestError?.response?.data?.message ||
          requestError?.message ||
          fallbackError,
      );
    } finally {
      setSavingState(false);
    }
  };

  const handleSaveSettings = () => saveStorefrontSettings();

  const handleCancelSettings = () => {
    if (!savedStorefrontFormRef.current) return;

    setSettingsError("");
    setFailedImages({});

    setSelectedCuisineTypes(
      parseCuisineTypes(savedStorefrontFormRef.current.cuisineType),
    );

    setStorefrontForm(savedStorefrontFormRef.current);
  };

  const handleCancelHours = () => {
    setSettingsError("");
    setHours(savedHoursRef.current);
  };

  const handleSaveHours = () =>
    saveStorefrontSettings({
      fallbackError: "Unable to save storefront hours.",
      setSavingState: setIsSavingHours,
    });

  /* ============================
     DRIVER QR API
  ============================ */

  useEffect(() => {
    let isCurrentRequest = true;

    const fetchDriverQr = async () => {
      setDriverQrError("");
      setIsLoadingDriverQr(true);

      try {
        const Authorization = userService.getJwt();
        const companyId = userService.getUserData().Compid;

        if (!companyId) {
          throw new Error("Company id was not found for this user.");
        }

        if (!Authorization) {
          throw new Error("Authorization was not found for this user.");
        }

        const qrImageUri = await getGeneratedQrImageUri(
          `${window.location.origin}/driver/vendor/${companyId}`,
        );

        if (!qrImageUri) {
          throw new Error("Driver QR code is not available yet.");
        }

        if (isCurrentRequest) {
          setDriverQrImageUri(qrImageUri);
        }
      } catch (requestError) {
        if (!isCurrentRequest) return;

        setDriverQrImageUri("");

        setDriverQrError(
          requestError?.response?.data?.message ||
            requestError?.message ||
            "Unable to load driver QR code.",
        );
      } finally {
        if (isCurrentRequest) {
          setIsLoadingDriverQr(false);
        }
      }
    };

    fetchDriverQr();

    return () => {
      isCurrentRequest = false;
    };
  }, []);

  const selectStorefrontTab = (tab) => {
    setActiveTab(tab);

    const nextUrl = new URL(window.location.href);

    nextUrl.searchParams.set("tab", tab);

    window.history.replaceState(null, "", nextUrl.toString());
  };

  const handleDownloadCustomerQr = async () => {
    if (!customerQrImageUri) return;

    await downloadQrWithBranding({
      fileName: `${storefrontForm.storeName || "store"}-customer-qr.png`,
      qrImageUri: customerQrImageUri,
      storeName: storefrontForm.storeName,
    });
  };

  const handlePrintCustomerQr = () => {
    if (!customerQrImageUri) return;

    const printWindow = window.open("", "_blank", "noopener,noreferrer");

    if (!printWindow) return;

    const language = getLanguage() === "ar" ? "ar" : "en";
    const storeName = storefrontForm.storeName || translateLabel("txt_web_vendor_store_fallback", "Store");
    const safeStoreName = escapePrintText(storeName);
    const printTitle = escapePrintText(translateLabel("txt_web_vendor_store_qr_print_title", "{store} QR Code", { store: storeName }));

    printWindow.document.write(`
      <!doctype html>
      <html lang="${language}" dir="${language === "ar" ? "rtl" : "ltr"}">
        <head>
          <title>${printTitle}</title>

          <style>
            body {
              margin: 0;
              min-height: 100vh;
              display: flex;
              align-items: center;
              justify-content: center;
              font-family: Arial, sans-serif;
            }

            .wrap {
              text-align: center;
              padding: 32px;
            }

            img {
              width: 280px;
              height: 280px;
              object-fit: contain;
            }

            h1 {
              margin: 0 0 24px;
              font-size: 28px;
            }
          </style>
        </head>

        <body>
          <div class="wrap">
            <h1>${safeStoreName}</h1>

            <img
              src="${customerQrImageUri}"
              alt="${escapePrintText(translateLabel("txt_web_vendor_customer_qr_code", "Customer QR Code"))}"
            />
          </div>

          <script>
            window.onload = function () {
              window.print();
            };
          </script>
        </body>
      </html>
    `);

    printWindow.document.close();
  };

  const handleDownloadDriverQr = async () => {
    if (!driverQrImageUri) return;

    await downloadQrWithBranding({
      fileName: `${storefrontForm.storeName || "store"}-driver-qr.png`,
      qrImageUri: driverQrImageUri,
      storeName: storefrontForm.storeName,
    });
  };

  const handlePrintDriverQr = () => {
    if (!driverQrImageUri) return;

    const printWindow = window.open("", "_blank", "noopener,noreferrer");

    if (!printWindow) return;

    const language = getLanguage() === "ar" ? "ar" : "en";
    const storeName = storefrontForm.storeName || translateLabel("txt_web_vendor_store_fallback", "Store");
    const safeStoreName = escapePrintText(storeName);
    const printTitle = escapePrintText(translateLabel("txt_web_vendor_driver_qr_print_title", "{store} Driver QR Code", { store: storeName }));

    printWindow.document.write(`
      <!doctype html>
      <html lang="${language}" dir="${language === "ar" ? "rtl" : "ltr"}">
        <head>
          <title>${printTitle}</title>

          <style>
            body {
              margin: 0;
              min-height: 100vh;
              display: flex;
              align-items: center;
              justify-content: center;
              font-family: Arial, sans-serif;
              background: #ffffff;
            }

            .wrap {
              text-align: center;
              padding: 32px;
            }

            img {
              width: 280px;
              height: 280px;
              object-fit: contain;
            }

            h1 {
              margin: 0 0 24px;
              font-size: 28px;
            }
          </style>
        </head>

        <body>
          <div class="wrap">
            <h1>${safeStoreName}</h1>

            <img
              src="${driverQrImageUri}"
              alt="${escapePrintText(translateLabel("txt_common_driver_qr_code", "Driver QR Code"))}"
            />
          </div>

          <script>
            window.onload = function () {
              window.print();
            };
          </script>
        </body>
      </html>
    `);

    printWindow.document.close();
  };

  useEffect(() => {
    const closeCuisineDropdown = (event) => {
      if (!cuisineDropdownRef.current?.contains(event.target)) {
        setIsCuisineDropdownOpen(false);
      }
    };

    document.addEventListener("mousedown", closeCuisineDropdown);

    return () => {
      document.removeEventListener("mousedown", closeCuisineDropdown);
    };
  }, []);

  useEffect(() => {
    let isCurrentRequest = true;

    const fetchSettings = async () => {
      setSettingsError("");

      try {
        const Authorization = userService.getJwt();
        const companyId = userService.getUserData().Compid;

        if (!companyId) {
          throw new Error("Company id was not found for this user.");
        }

        const response = await vendorStorefrontController.getSettings({
          Authorization,
          companyId,
        });

        if (response?.isAxiosError || response?.response) {
          throw response;
        }

        const payload = unwrapPayload(response);

        if (!isCurrentRequest) return;

        applySettingsPayload(payload, companyId);
      } catch (requestError) {
        if (!isCurrentRequest) return;

        setSettingsError(
          requestError?.response?.data?.message ||
            requestError?.message ||
            "Unable to load storefront settings.",
        );
      }
    };

    fetchSettings();

    return () => {
      isCurrentRequest = false;
    };
  }, []);

  return (
    <VendorLayout activeKey="storefront">
      <section className="vendor-storefront-page">
        <input
          ref={logoInputRef}
          className="vendor-storefront-file-input"
          type="file"
          accept="image/*"
          onChange={updateImageField("logoUrl", {
            maxDataUrlLength: 6000,
            outputHeight: 128,
            outputWidth: 128,
            quality: 0.24,
          })}
        />

        <input
          ref={coverInputRef}
          className="vendor-storefront-file-input"
          type="file"
          accept="image/*"
          onChange={updateImageField("coverImageUrl", {
            maxDataUrlLength: 9000,
            outputHeight: 100,
            outputWidth: 300,
            quality: 0.24,
          })}
        />

        <div className="vendor-storefront-header">
          <div className="vendor-menu-heading">
            <div>
              <div className="vendor-storefront-page-title"> {translateLabel("txt_storefront_settings", "Storefront Settings")} </div>

              <div className="vendor-storefront-page-subtitle">
                {translateLabel("txt_storefront_settings_description", "Manage how your business appears to customers")}
              </div>
            </div>
          </div>

          <div
            className={`vendor-store-online ${
              storeOnline ? "online" : "offline"
            }`}
            role="status"
          >
            <div aria-hidden="true" />

            {storeOnline ? translateLabel("txt_store_online", "Store Online") : translateLabel("txt_web_vendor_store_closed", "Store Closed")}
          </div>
        </div>
        <div className="vendor-storefront-tabs" role="tablist">
          {STOREFRONT_TABS.map(({ label, icon: TabIcon }) => {
            const isSelected = activeTab === label;

            return (
              <GloablButton
                className={`vendor-storefront-tab ${
                  isSelected ? "active" : ""
                }`}
                width="100%"
                height="25px"
                background={isSelected ? "#fa9c03" : "#2b1f1a"}
                borderRadius="6px"
                fontSize="10px"
                fontWeight={700}
                lineHeight="14px"
                padding="0 8px"
                text={translateText(label)}
                icon={<TabIcon />}
                key={label}
                onClick={() => selectStorefrontTab(label)}
              />
            );
          })}
        </div>

        {activeTab === "General" && (
          <section className="vendor-storefront-card vendor-storefront-general-card">
            <div className="vendor-storefront-section-title">
              {translateLabel("txt_store_information", "Store Information")}
            </div>

            <div className="vendor-storefront-section-subtitle">
              {translateLabel("txt_store_information_description", "Basic details about your business")}
            </div>

            {settingsError ? (
              <div className="vendor-storefront-error" role="alert">{translateStorefrontFeedback(settingsError)}</div>
            ) : null}

            <label className="vendor-storefront-checkbox-row">
              {/* <input type="checkbox" checked readOnly /> */}

              <span> {translateLabel("txt_store_name_approval_notice", "Store name changes require admin approval and may take 24-48 hours to process.")} </span>
            </label>

            <div className="vendor-storefront-general-grid">
              <label className="vendor-storefront-field">
                <span>{translateLabel("input_store_name", "Store Name")}</span>

                <input
                  value={storefrontForm.storeName}
                  onChange={updateFormField("storeName")}
                />
              </label>

              <div
                className="vendor-storefront-field vendor-storefront-cuisine-field"
                ref={cuisineDropdownRef}
              >
                <div className="vendor-storefront-cuisine-label">
                  {translateText(storeTypeLabel)}
                </div>

                <div className="vendor-storefront-cuisine-select">
                  <div
                    className={`vendor-storefront-cuisine-trigger ${
                      isCuisineDropdownOpen ? "open" : ""
                    }`}
                    role="button"
                    aria-label={translateText(storeTypeLabel)}
                    tabIndex={0}
                    aria-expanded={isCuisineDropdownOpen}
                    onClick={() =>
                      setIsCuisineDropdownOpen((isOpen) => !isOpen)
                    }
                    onKeyDown={(event) => {
                      if (event.key === "Enter" || event.key === " ") {
                        event.preventDefault();

                        setIsCuisineDropdownOpen((isOpen) => !isOpen);
                      }

                      if (event.key === "Escape") {
                        setIsCuisineDropdownOpen(false);
                      }
                    }}
                  >
                    <div>
                      {getStoreTypeLabel(
                        selectedCuisineTypes.join(CUISINE_TYPE_SEPARATOR),
                        isGroceryVendor,
                      )}
                    </div>

                    <i aria-hidden="true" />
                  </div>

                  {isCuisineDropdownOpen && (
                    <div
                      className="vendor-storefront-cuisine-menu"
                      role="listbox"
                      aria-multiselectable="true"
                    >
                      {storeTypeGroups.map((group) => {
                        const isGroupOpen = openCuisineGroups.includes(
                          group.title,
                        );

                        return (
                          <div
                            className="vendor-storefront-cuisine-group"
                            key={group.title}
                          >
                            <div
                              className="vendor-storefront-cuisine-group-header"
                              role="button"
                              tabIndex={0}
                              aria-expanded={isGroupOpen}
                              onClick={() => toggleCuisineGroup(group.title)}
                              onKeyDown={(event) => {
                                if (
                                  event.key === "Enter" ||
                                  event.key === " "
                                ) {
                                  event.preventDefault();

                                  toggleCuisineGroup(group.title);
                                }
                              }}
                            >
                              <div>{translateText(group.title)}</div>
                              <i aria-hidden="true" />
                            </div>

                            {isGroupOpen &&
                              group.items.map((item) => (
                                <div
                                  className={`vendor-storefront-cuisine-option ${
                                    selectedCuisineTypes.includes(item.label)
                                      ? "selected"
                                      : ""
                                  }`}
                                  key={item.label}
                                  role="option"
                                  tabIndex={0}
                                  aria-selected={selectedCuisineTypes.includes(
                                    item.label,
                                  )}
                                  onClick={() => selectCuisineType(item.label)}
                                  onKeyDown={(event) => {
                                    if (
                                      event.key === "Enter" ||
                                      event.key === " "
                                    ) {
                                      event.preventDefault();

                                      selectCuisineType(item.label);
                                    }
                                  }}
                                >
                                  <div>{translateText(item.label)}</div>

                                  {item.marker && (
                                    <div
                                      className={
                                        item.type === "flag"
                                          ? `vendor-storefront-cuisine-flag vendor-storefront-cuisine-flag-${item.marker}`
                                          : "vendor-storefront-cuisine-badge"
                                      }
                                      aria-hidden="true"
                                    >
                                      {item.type === "badge" ? item.marker : ""}
                                    </div>
                                  )}
                                </div>
                              ))}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>

              <label className="vendor-storefront-field vendor-storefront-field-full">
                <span> {translateLabel("input_business_description_settings", "Business Description")}{" "}
                </span>

                <textarea
                  maxLength={1000}
                  placeholder={translateLabel("txt_web_vendor_tell_customers_about_your_business", "Tell customers about your business...")}
                  value={storefrontForm.businessDescription}
                  onChange={updateFormField("businessDescription")}
                />
                <em className="vendor-character-counter">
                  <bdi>{translateLabel("txt_web_vendor_description_character_count", "{count} / {limit} characters", { count: storefrontForm.businessDescription.length, limit: 1000 })}</bdi>
                </em>
              </label>

              <label className="vendor-storefront-field">
                <span>{translateLabel("input_price_range", "Price Range")}</span>

                <select
                  value={storefrontForm.priceRange}
                  onChange={updateFormField("priceRange")}
                >
                  <option value="">{translateLabel("txt_web_vendor_select_price_range", "Select price range")}</option>

                  <option value="$ - Budget">{translateLabel("input_price_range_budget", "$ - Budget")}</option>

                  <option value="$$ - Moderate">{translateLabel("input_price_range_moderate", "$$ - Moderate")}</option>

                  <option value="$$$ - Premium">{translateLabel("txt_web_vendor_premium", "$$$ - Premium")}</option>
                </select>
              </label>
            </div>

            <div className="vendor-storefront-actions">
              {/* <GloablButton
                {...CANCEL_BUTTON_PROPS}
                className="vendor-storefront-cancel-button"
                onClick={handleCancelSettings}
                text="Cancel"
              /> */}

              <GloablButton
                {...SAVE_CHANGES_BUTTON_PROPS}
                className="vendor-storefront-save-button"
                disabled={isSavingSettings}
                onClick={handleSaveSettings}
                text={translateText(isSavingSettings ? "Saving..." : "Save Changes")}
              />
            </div>
          </section>
        )}

        {activeTab === "Branding" && (
          <section className="vendor-storefront-card vendor-storefront-branding-card">
            <div className="vendor-storefront-section-title">
              {translateLabel("txt_visual_identity", "Visual Identity")}
            </div>

            <div className="vendor-storefront-section-subtitle">
              {translateLabel("txt_visual_identity_description", "Upload your logo and cover image")}
            </div>

            <div className="vendor-storefront-upload-stack">
              <div className="vendor-storefront-upload-row">
                <div>
                  <div className="vendor-storefront-upload-label">
                    {translateLabel("input_store_logo", "Store Logo")}
                  </div>

                  {renderLogoUpload()}
                </div>

                <div className="vendor-storefront-upload-help">
                  <GloablButton
                    {...UPLOAD_MEDIA_BUTTON_PROPS}
                    className="vendor-storefront-upload-button vendor-storefront-upload-logo-button"
                    onClick={triggerLogoUpload}
                    text={translateLabel("btn_upload_new_logo", "Upload New Logo")}
                  />

                  <span> {translateLabel("txt_web_vendor_recommended_square_image_minimum_512x512px_max_5mb", "Recommended: Square image, minimum 512x512px, max 5MB.")} </span>
                </div>
              </div>

              <div className="vendor-storefront-upload-row">
                <div>
                  <div className="vendor-storefront-upload-label">
                    {translateLabel("input_cover_image", "Cover Image")}
                  </div>

                  {renderCoverUpload()}
                </div>

                <div className="vendor-storefront-upload-help">
                  <GloablButton
                    {...UPLOAD_MEDIA_BUTTON_PROPS}
                    className="vendor-storefront-upload-button vendor-storefront-upload-cover-button"
                    onClick={triggerCoverUpload}
                    text={translateLabel("btn_upload_cover_image", "Upload Cover Image")}
                  />

                  <span>{translateLabel("txt_web_vendor_recommended_1200x400px_max_5mb", "Recommended: 1200x400px, max 5MB.")}</span>
                </div>
              </div>
            </div>

            <div className="vendor-storefront-actions">
              {/* <GloablButton
                {...CANCEL_BUTTON_PROPS}
                className="vendor-storefront-cancel-button"
                onClick={handleCancelSettings}
                text="Cancel"
              /> */}

              <GloablButton
                {...SAVE_CHANGES_BUTTON_PROPS}
                className="vendor-storefront-save-button"
                disabled={isSavingSettings}
                onClick={handleSaveSettings}
                text={translateText(isSavingSettings ? "Saving..." : "Save Changes")}
              />
            </div>
          </section>
        )}

        {activeTab === "Hours & Availability" && (
          <section className="vendor-storefront-card vendor-storefront-hours-card">
            <div className="vendor-storefront-hours-list">
              {WEEK_DAYS.map((day) => (
                <div className="vendor-storefront-hours-row" key={day}>
                  <div className="vendor-storefront-hours-row-main">
                    <div className="vendor-storefront-day">{translateText(day)}</div>

                    <div className="vendor-storefront-time-field">
                      <input
                        type="time"
                        aria-label={translateLabel("txt_web_vendor_opening_time_day", "Opening time for {day}", { day: translateText(day) })}
                        value={hours[day].start}
                        placeholder=""
                        onChange={(event) =>
                          updateHour(day, "start", event.target.value)
                        }
                      />
                    </div>

                    <span>{translateLabel("txt_time_to", "to")}</span>

                    <div className="vendor-storefront-time-field">
                      <input
                        type="time"
                        aria-label={translateLabel("txt_web_vendor_closing_time_day", "Closing time for {day}", { day: translateText(day) })}
                        value={hours[day].end}
                        placeholder=""
                        onChange={(event) =>
                          updateHour(day, "end", event.target.value)
                        }
                      />
                    </div>

                    <div className="vendor-storefront-hours-status">
                      <div
                        className={`vendor-storefront-small-toggle ${
                          hours[day].open ? "active" : ""
                        }`}
                        role="switch"
                        aria-label={translateLabel("txt_web_vendor_open_on_day", "Open on {day}", { day: translateText(day) })}
                        aria-checked={hours[day].open}
                        tabIndex={0}
                        onClick={() =>
                          updateHour(day, "open", !hours[day].open)
                        }
                        onKeyDown={(event) => {
                          if (event.key === "Enter" || event.key === " ") {
                            event.preventDefault();

                            updateHour(day, "open", !hours[day].open);
                          }
                        }}
                      >
                        <div />
                      </div>

                      <div className="vendor-storefront-open-label">
                        {hours[day].open ? translateLabel("txt_web_vendor_open", "Open") : translateLabel("txt_web_vendor_closed", "Closed")}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <div className="vendor-storefront-actions">
              {/* <GloablButton
                {...CANCEL_BUTTON_PROPS}
                className="vendor-storefront-cancel-button"
                onClick={handleCancelHours}
                text="Cancel"
              /> */}

              <GloablButton
                {...SAVE_CHANGES_BUTTON_PROPS}
                className="vendor-storefront-save-button"
                disabled={isSavingHours}
                onClick={handleSaveHours}
                text={translateText(isSavingHours ? "Saving..." : "Save Changes")}
              />
            </div>
          </section>
        )}

        {activeTab === "Operations" && (
          <div className="vendor-storefront-operation-stack">
            <section className="vendor-storefront-card vendor-storefront-operations-settings-card">
              <div className="vendor-storefront-section-title">
                {translateLabel("txt_operational_settings", "Operational Settings")}
              </div>

              <div className="vendor-storefront-section-subtitle">
                {translateLabel("txt_operational_settings_description", "Configure order handling and capacity")}
              </div>

              <label className="vendor-storefront-field">
                <span>{translateLabel("input_preparation_time", "Preparation Time (minutes)")}</span>

                <select
                  value={storefrontForm.preparationTimeMinutes}
                  onChange={updateFormField("preparationTimeMinutes")}
                >
                  <option value="">{translateLabel("txt_web_vendor_select_preparation_time", "Select preparation time")}</option>

                  <option value="30">{translateLabel("txt_web_vendor_15_30_minutes", "15-30 minutes")}</option>

                  <option value="45">{translateLabel("txt_web_vendor_30_45_minutes", "30-45 minutes")}</option>

                  <option value="60">{translateLabel("txt_web_vendor_45_60_minutes", "45-60 minutes")}</option>
                </select>
              </label>

              <div className="vendor-storefront-actions">
                {/* <GloablButton
                  {...CANCEL_BUTTON_PROPS}
                  className="vendor-storefront-cancel-button"
                  onClick={handleCancelSettings}
                  text="Cancel"
                /> */}

                <GloablButton
                  {...SAVE_CHANGES_BUTTON_PROPS}
                  className="vendor-storefront-save-button"
                  disabled={isSavingSettings}
                  onClick={handleSaveSettings}
                  text={translateText(isSavingSettings ? "Saving..." : "Save Changes")}
                />
              </div>
            </section>
          </div>
        )}

        {activeTab === "QR Codes" && (
          <section className="vendor-storefront-qr-section">
            <div className="vendor-storefront-section-title">{translateLabel("txt_web_vendor_your_qr_codes", "Your QR Codes")}</div>

            <div className="vendor-storefront-section-subtitle">
              {translateLabel("txt_web_vendor_use_these_qr_codes_for_your_store_operations", "Use these QR codes for your store operations.")}
            </div>

            <div className="vendor-storefront-qr-grid">
              {STORE_QR_CARDS.map(
                ({ type, title, description, icon: CardIcon }) => {
                  const isCustomerQr = type === "customer";

                  const qrImageUri = isCustomerQr
                    ? customerQrImageUri
                    : driverQrImageUri;

                  const hasQr = Boolean(qrImageUri);

                  const isLoadingQr = !isCustomerQr && isLoadingDriverQr;

                  const qrError = !isCustomerQr ? driverQrError : "";

                  return (
                    <article className="vendor-storefront-qr-card" key={title}>
                      <div className="vendor-storefront-qr-card-header">
                        <span className="vendor-storefront-qr-card-icon">
                          <CardIcon />
                        </span>

                        <div>{translateText(title)}</div>
                      </div>

                      <p>{translateText(description)}</p>

                      <div className="vendor-storefront-qr-preview">
                        {hasQr ? (
                          <div className="vendor-storefront-qr-real">
                            <div className="vendor-storefront-qr-store-name">
                              {storefrontForm.storeName || translateLabel("btn_vendor", "Vendor")}
                            </div>

                            <img
                              alt={
                                translateText(isCustomerQr
                                  ? "Customer QR Code"
                                  : "Driver QR Code")
                              }
                              className="vendor-storefront-qr-image"
                              src={qrImageUri}
                            />

                            <div className="vendor-storefront-qr-logo">
                              <LogoDakikaNos />
                            </div>
                          </div>
                        ) : (
                          <div>
                            <StorefrontQrCodeIcon />

                            {isLoadingQr && (
                              <div className="vendor-storefront-section-subtitle">
                                {translateLabel("txt_web_vendor_loading_driver_qr", "Loading Driver QR...")}
                              </div>
                            )}

                            {!isLoadingQr && qrError && (
                              <div className="vendor-storefront-section-subtitle" role="alert">
                                {translateStorefrontFeedback(qrError)}
                              </div>
                            )}
                          </div>
                        )}
                      </div>

                      <div className="vendor-storefront-qr-actions">
                        <GloablButton
                          className="vendor-storefront-qr-download"
                          width="96px"
                          height="30px"
                          background="#ffffff"
                          border="1px solid #d9dee7"
                          borderRadius="6px"
                          color="#000000"
                          disabled={!hasQr}
                          fontSize="12px"
                          fontWeight={500}
                          lineHeight="15px"
                          onClick={
                            isCustomerQr
                              ? handleDownloadCustomerQr
                              : handleDownloadDriverQr
                          }
                          padding="0 12px"
                          text={translateLabel("btn_download", "Download")}
                        />

                        <GloablButton
                          className="vendor-storefront-qr-print"
                          width="100px"
                          height="30px"
                          background="#fb8529"
                          borderRadius="6px"
                          color="#ffffff"
                          disabled={!hasQr}
                          fontSize="12px"
                          fontWeight={500}
                          lineHeight="15px"
                          onClick={
                            isCustomerQr
                              ? handlePrintCustomerQr
                              : handlePrintDriverQr
                          }
                          padding="0 12px"
                          text={translateLabel("btn_print", "Print")}
                        />
                      </div>
                    </article>
                  );
                },
              )}
            </div>
          </section>
        )}
      </section>
    </VendorLayout>
  );
}

export default VendorStorefront;
